import { useRef } from 'react';
import { useEffect } from 'react';

import type { EventSourcePolyfill } from 'event-source-polyfill';

import type { UUID } from '@cobliteam/common-types';

import { FleetEventSourceRequester } from '@cobliteam/api-lib/src/dashboard/device/FleetEventSourceRequester';
import type { DeviceStatusEvent } from '@cobliteam/api-lib/src/dashboard/device/definitions';
import { EventSourceRequester } from '@cobliteam/api-lib/src/request/EventSourceRequester';

import { DatadogTrack } from '@cobliteam/datadog-rum';
import { Environment } from '@cobliteam/react-environments';

import { QUERY_KEY, useSetQueryDataDevices } from '../useEntity/useDevices';
import useUser from '../useEntity/useUser';
import useFeatureFlags from '../useFeatureFlags';

const env = Environment.getInstance().getEnvironment();
const EVENTS_THRESHOLD = 100;

let eventsAmount = 0;

function useSSE() {
  const { getSession } = useUser();
  const session = getSession();
  const deviceStatusEventSourceRef = useRef<EventSource | EventSourcePolyfill | undefined>(undefined);
  const { libraryExtensionEventSource } = useFeatureFlags();
  const fleetEventSource = new FleetEventSourceRequester({
    host: env.apiHost,
    useEventSourceLibrary: libraryExtensionEventSource,
  });
  const setDevices = useSetQueryDataDevices(() => {
    eventsAmount += 1;
  });

  const subscribeToDeviceStatus = (fleetId: UUID, vehicleGroupIds?: UUID[]) => {
    const eventDataUpdated = (eventData: DeviceStatusEvent[]) => {
      const groupIds = vehicleGroupIds?.join(',');
      const queryKey = groupIds ? [QUERY_KEY, groupIds] : [QUERY_KEY];

      setDevices({ queryKey, data: eventData });
    };

    return fleetEventSource.subscribeToDeviceStatus(fleetId, eventDataUpdated, vehicleGroupIds);
  };

  const unsubscribeFromFleetEvents = (eventSource: EventSource | EventSourcePolyfill) => {
    EventSourceRequester.unsubscribe(eventSource);
  };

  const startSSE = (vehicleGroupIds?: UUID[]) => {
    subscribeToDeviceStatus(session.fleet.id, vehicleGroupIds).then((eventSource) => {
      deviceStatusEventSourceRef.current = eventSource;
    });
  };

  const stopSSE = () => {
    if (deviceStatusEventSourceRef.current) unsubscribeFromFleetEvents(deviceStatusEventSourceRef.current);
  };

  const restartSSE = (vehicleGroupIds?: UUID[]) => {
    stopSSE();
    startSSE(vehicleGroupIds);
  };

  useEffect(() => {
    return () => stopSSE();
  }, []);

  useEffect(() => {
    if (eventsAmount >= EVENTS_THRESHOLD) {
      DatadogTrack.action('Devices updated from SSE');
      eventsAmount = 0;
    }
  }, [eventsAmount]);

  return {
    startSSE,
    restartSSE,
  };
}

export default useSSE;
